<template>
  <div
    class="rs-form-file"
    :class="inputClasses"
  >
    <!-- Default Bootstrap Variant -->
    <div v-show="visible && variant === 'bootstrap'">
      <label
        v-if="label"
        :for="id"
      >{{ label }}<sup v-if="required">*</sup></label>
      <span v-if="helpTooltip">
        <RsIcon
          :id="`helperTooltip-${id}`"
          icon="question-circle"
          class="tooltip-question-icon"
        />
        <RsTooltip
          :target="`helperTooltip-${id}`"
          :title="helpTooltip"
          :position="helpTooltipPosition"
          :delay="{ show: 200 }"
        />
      </span>
      <div
        v-if="imported"
        class="import-tooltip"
        :class="{ 'import-tooltip--open': importHover }"
      >
        <p>This field is managed by <strong>[Data Source]</strong>.<br>Changing this will be temporary.</p>
      </div>
      <div class="input-group" role="group">
        <div
          v-if="imported"
          class="input-group-prepend"
          @click="toggleState"
          @mouseover="importHover = true"
          @mouseleave="importHover = false"
        >
          <span class="input-group-text">
            <RsIcon
              :icon="isUnlocked ? 'lock-open-alt' : 'lock-alt'"
            />
          </span>
        </div>
        <div class="custom-file">
          <input
            :id="id"
            ref="bFormFile"
            type="file"
            class="custom-file-input"
            :accept="accept"
            :multiple="multiple"
            :disabled="readonly || (imported && isUnlocked == false) || disabled"
            :data-browse="$ts('fileInput.browseButton')"
            @change="onUploadStart(($event.target as HTMLInputElement).files!)"
          />
          <label class="custom-file-label" :for="id">{{ placeholder }}</label>
        </div>
      </div>
      <div
        v-show="toggleFileName"
      >
        <p
          :key="fileKey + 2"
          class="btn__text--icon break-text"
        >
          {{ currentFileName || fileName }}
          <i
            :id="removeButtonId"
            :key="removeButtonId"
            :title="$ts('actions.removeItem')"
            :aria-label="$ts('actions.removeItem')"
            style="color:#d92929;"
            class="pl-1 far fa-times-circle clickable-pointer"
            @click="resetFile()"
          />
          <i v-if="hasPreview" :id="previewButtonId" :key="previewButtonId" class="pl-2 fa fa-eye clickable-pointer" @click="viewFile" />
          <RsTooltip
            :key="`${previewButtonId}-tooltip`"
            :target="previewButtonId"
            :title="$ts('actions.preview')"
            position="top"
            variant="inline rs-form-file-action-tooltip"
            :delay="{ show: 200 }"
          />
          <i v-if="hasPdfDownload" :id="downloadButtonId" :key="downloadButtonId" class="pl-2 fa-sharp fa-solid fa-arrow-down-to-line clickable-pointer" @click="downloadPDF" />
          <RsTooltip
            :key="`${downloadButtonId}-tooltip`"
            :target="downloadButtonId"
            :title="$ts('actions.download')"
            position="top"
            variant="inline rs-form-file-action-tooltip"
            :delay="{ show: 200 }"
          />
        </p>
      </div>
      <FsLightbox
        v-if="hasPreview && imageUrl"
        :key="lightboxKey"
        :toggler="showLightBox"
        :sources="[imageUrl]"
      />
    </div>

    <!-- Button Variant -->
    <div v-show="visible && variant === 'btn'">
      <label
        v-if="label"
        :for="id"
      >{{ label }}<sup v-if="required">*</sup></label>
      <input
        :id="id"
        ref="inputFormFile"
        type="file"
        name="filenamed"
        hidden
        :accept="accept"
        :multiple="multiple"
        @change="onUploadStart(($event.target as HTMLInputElement).files!)"
      >
      <!-- :key="promptKey + 1" -->
      <div>
        <label
          v-show="!toggleFileName"
          class="btn__plain"
          :for="id"
        >
          {{ prompt }}
        </label>
        <!-- :key="promptKey" -->
        <!-- :click="resetFile()" -->

        <div
          v-show="toggleFileName"
          @click="resetFile()"
        >
          <p
            :key="fileKey + 2"
            class="btn__text--icon pt-2"
          >
            {{ currentFileName || fileName }}
            <i
              style="color:#d92929; "
              class="far fa-times-circle "
            />
          </p>
        </div>
      </div>
    </div>

    <!-- Text variant -->
    <div v-show="visible && variant === 'text'">
      <label
        v-if="label"
        :for="id"
      >{{ label }}<sup v-if="required">*</sup></label>
      <input
        :id="id"
        ref="inputFormFile"
        type="file"
        name="filename"
        hidden
        :accept="accept"
        :multiple="multiple"
        @change="onUploadStart(($event.target as HTMLInputElement).files!)"
      >
      <div>
        <label
          v-show="!toggleFileName"
          class="btn__text"
          :for="id"
        >
          {{ prompt }}
        </label>
        <!-- :key="promptKey" -->
        <!-- :click="resetFile()" -->

        <div
          v-show="toggleFileName"
          @click="resetFile()"
        >
          <p
            :key="fileKey + 2"
            class="btn__text--icon pt-2"
          >
            {{ currentFileName || fileName }}
            <i
              style="color:#d92929; "
              class="far fa-times-circle "
            />
          </p>
        </div>
      </div>
    </div>

    <!-- Pencil btn variant -->
    <div v-show="visible && variant === 'pencil'">
      <input
        :id="id"
        ref="inputFormFile"
        type="file"
        name="filename"
        hidden
        :accept="accept"
        :multiple="multiple"
        @change="onUploadStart(($event.target as HTMLInputElement).files!)"
      >
      <RsButton
        class="edit-button"
        variant="light"
        icon="pencil"
        @click="($refs.inputFormFile as HTMLInputElement).click()"
      />
    </div>

    <!-- Inline Button Variant -->
    <div v-show="visible && variant === 'inline-btn'">
      <input
        :id="id"
        ref="inputFormFile"
        type="file"
        name="filenamed"
        hidden
        :accept="accept"
        :multiple="multiple"
        @change="onUploadStart(($event.target as HTMLInputElement).files!)"
      >
      <div>
        <label
          class="btn__plain"
          :for="id"
        >
          {{ prompt }}
        </label>
      </div>
    </div>
    <div
      v-if="uploadFileTypeError"
      class="text--error"
    >
      {{ uploadFileTypeError }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import FsLightbox from 'fslightbox-vue';
import type { TTooltipPosition } from '@/shared/types';
import { assertRef } from '@/shared/helpers/assert-ref';
import i18n from '@/i18n';

export default defineComponent({
  name: 'RsFormFile',
  components: { FsLightbox },
  props: {
    prompt: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'bootstrap',
      validator: (value?: string) => [
        'btn',
        'text',
        'bootstrap',
        'pencil',
        'inline-btn',
      ].indexOf(value ?? 'bootstrap') !== -1
      ,
    },

    id: {
      type: String,
      default: null,
    },

    currentFileName: {
      type: String,
      default: null,
    },

    visible: {
      type: Boolean,
      default: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: 'image/jpeg, image/png, image/gif, image/webp',
    },
    /**
     *  Used to create a read-only input with the option to edit for imported fields.
     *
     */
    imported: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },
    fileType: {
      type: String,
      default: '',
    },
    hasPreview: {
      type: Boolean,
      default: false,
    },
    hasPdfDownload: {
      type: Boolean,
      default: false,
    },
    imageUrl: {
      type: String,
      default: '',
    },
    pdfUrl: {
      type: String,
      default: '',
    },
    helpTooltip: {
      type: String,
      default: '',
    },
    helpTooltipPosition: {
      type: String as PropType<TTooltipPosition>,
      default: 'top',
    },
  },
  data() {
    return {
      isUnlocked: false as boolean,
      importHover: false as boolean,
      fileName: '' as string,
      fileKey: 0,
      showLightBox: false as boolean,
      lightboxKey: 0 as number,
      uploadFileTypeError: '' as string,
    };
  },

  computed: {
    toggleFileName(): boolean {
      return this.currentFileName?.length > 0 || !!this.fileName;
    },

    inputClasses(): Record<string, boolean> {
      return {
        'rs-input--imported': this.imported,
        'rs-input--unlocked': this.isUnlocked,
      };
    },
    placeholder(): string {
      return this.currentFileName || this.fileName || i18n.t('fileInput.noFileChosen') as string;
    },
    tooltipIdBase(): string {
      return `rs-form-file-${this.$.uid}`;
    },
    removeButtonId(): string {
      return `${this.tooltipIdBase}-remove-btn`;
    },
    previewButtonId(): string {
      return `${this.tooltipIdBase}-preview-btn`;
    },
    downloadButtonId(): string {
      return `${this.tooltipIdBase}-download-btn`;
    },
  },

  watch: {
    currentFileName(newVal: string, oldVal: string) {
      if (!oldVal && newVal && this.variant === 'bootstrap') this.resetNativeFileInput();
    },
    imageUrl() {
      this.lightboxKey++;
    },
  },

  methods: {
    resetNativeFileInput(): void {
      const el = this.$refs.bFormFile as HTMLInputElement | null;
      if (el) el.value = '';
    },
    resetFile(): void {
      if (this.variant === 'bootstrap') this.resetNativeFileInput();
      this.fileKey = 0;
      this.fileName = '';
      this.uploadFileTypeError = '';
      this.$emit('on-file-type-error', null);
      this.$emit('on-file-reset', this.fileName || this.currentFileName);
    },
    onUploadStart(files: FileList): void {
      const { length } = files;

      if (length > 0) {
        const fileDetails = [];
        this.fileName = files[0].name;
        this.fileKey++;

        for (let i = 0; i < length; i += 1) {
          if (this.fileType && !files[i].type.includes(this.fileType)) {
            const readableType = i18n.t(`fileInput.readableTypes.${this.fileType}`) as string
              || `${this.fileType} files`;

            this.uploadFileTypeError = i18n.t('fileInput.unsupportedFileType', { type: readableType }) as string;
            this.$emit('on-file-type-error', this.uploadFileTypeError);
            return;
          }

          fileDetails.push({
            lastModified: files[i].lastModified,
            name: files[i].name,
            size: files[i].size,
            type: files[i].type,
          });
        }

        this.uploadFileTypeError = '';
        this.$emit('on-file-type-error', null);
        this.$emit('on-upload-triggered', { files, fileDetails });
      }
    },
    onClickUpload(): void {
      if (this.$refs.inputFormFile) assertRef(this.$refs.inputFormFile, HTMLInputElement)?.click();
    },
    toggleState(): void {
      this.isUnlocked = !this.isUnlocked;
    },
    viewFile() {
      if (this.fileType === 'pdf') {
        window.open(this.pdfUrl, '_blank', 'noopener,noreferrer');
      }
      if (this.fileType === 'image') {
        this.showLightBox = !this.showLightBox;
      }
    },
    downloadPDF() {
      fetch(this.pdfUrl).then((t) => t.blob().then((b) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(b);
        a.setAttribute('download', this.currentFileName);
        a.click();
      }));
    },
  },
});
</script>
<style lang="scss">
@import '@/styles/_variables.scss';
.rs-form-file{
  .custom-file-input,
  .custom-file-label{
    height: 40px;
  }
  .custom-file-label{
    display: flex;
    align-items: center;
    border-color: $tertiary-gray;
    border-radius: $global-radius;
    &::after{
      content: attr(data-browse);
      cursor: pointer;
      display: flex;
      align-items: center;
      height: 38px;
      background-color: $secondary-color;
      color: $white;
      font-weight: 600;
      transition: background-color 0.2s ease;
    }
    &:hover{
      &::after{
        background-color: $secondary-color-dark;
      }
    }
  }
  .btn {
    &__text {
      padding: 0.5rem 0rem;
      font-family: sans-serif;
      border-radius: 0.3rem;
      font-weight: 700;
      max-height: 3rem;

      &--icon {
        margin: 5px 0px;
        font-weight: 700;
      }
    }
    &__plain {
      background-color: $secondary-color;
      color: white;
      padding: 0.5rem 1rem;
      font-family: sans-serif;
      border-radius: 0.3rem;
      cursor: pointer;
      max-height: 3rem;
      font-weight: 500;
    }
  }
  .clickable-pointer {
    cursor: pointer;
  }
}
</style>
